import { apiUrl } from "@/lib/apiBase";
import { getAccessToken } from "@/lib/authSession";
import { inferWorkspaceFromPath } from "@/scripts/OrgFilterContext";

export const ANALYTICS_OPT_OUT_KEY = "circa.analyticsOptOut";
export const ANALYTICS_BANNER_KEY = "circa.analyticsBannerDismissed";
export const ANALYTICS_VISITOR_KEY = "circa.analyticsVisitorId";
export const ANALYTICS_SESSION_KEY = "circa.analyticsSessionId";
export const ANALYTICS_SESSION_AT_KEY = "circa.analyticsSessionAt";

const SESSION_IDLE_MS = 30 * 60 * 1000;
const FLUSH_MS = 2000;
const MAX_BATCH = 20;

let queue = [];
let flushTimer = null;
let lastPathTracked = null;
let sessionStartSent = false;

function lsGet(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function lsSet(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

export function isAnalyticsOptedOut() {
  return lsGet(ANALYTICS_OPT_OUT_KEY) === "1";
}

export function setAnalyticsOptOut(optOut) {
  if (optOut) {
    lsSet(ANALYTICS_OPT_OUT_KEY, "1");
    queue = [];
  } else {
    try {
      localStorage.removeItem(ANALYTICS_OPT_OUT_KEY);
    } catch {
      /* ignore */
    }
  }
}

export function isAnalyticsBannerDismissed() {
  return lsGet(ANALYTICS_BANNER_KEY) === "1";
}

export function dismissAnalyticsBanner() {
  lsSet(ANALYTICS_BANNER_KEY, "1");
}

function mintId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `v_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export function getVisitorId() {
  let id = lsGet(ANALYTICS_VISITOR_KEY);
  if (!id) {
    id = mintId();
    lsSet(ANALYTICS_VISITOR_KEY, id);
  }
  return id;
}

export function getSessionId() {
  const now = Date.now();
  const prevAt = Number(lsGet(ANALYTICS_SESSION_AT_KEY) || 0);
  let id = lsGet(ANALYTICS_SESSION_KEY);
  if (!id || !prevAt || now - prevAt > SESSION_IDLE_MS) {
    id = mintId();
    lsSet(ANALYTICS_SESSION_KEY, id);
    sessionStartSent = false;
  }
  lsSet(ANALYTICS_SESSION_AT_KEY, String(now));
  return id;
}

/**
 * Classify route into public | workplace | social.
 * @param {string} pathname
 * @param {boolean} isAuthenticated
 */
export function resolveAnalyticsSurface(pathname, isAuthenticated) {
  const p = String(pathname || "");
  if (!isAuthenticated) return "public";
  if (/^\/orgs\/[^/]+\/social(?:\/|$)/.test(p)) return "social";
  const inferred = inferWorkspaceFromPath(p);
  if (inferred === "social") return "social";
  if (
    p === "/about" ||
    p.startsWith("/invite/") ||
    p === "/login" ||
    p === "/signup" ||
    p === "/verify-email"
  ) {
    return "public";
  }
  if (p === "/search" || p.startsWith("/search?")) {
    return "social";
  }
  return "workplace";
}

function scheduleFlush() {
  if (flushTimer != null) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushAnalytics();
  }, FLUSH_MS);
}

/**
 * @param {{ name: string, surface: string, path?: string, props?: object, org_id?: string }} event
 */
export function trackAnalyticsEvent(event) {
  if (typeof window === "undefined") return;
  if (isAnalyticsOptedOut()) return;
  if (!event?.name || !event?.surface) return;
  getSessionId(); // refresh idle timer / mint session before enqueue
  if (!sessionStartSent) {
    sessionStartSent = true;
    queue.push({
      name: "session_start",
      surface: event.surface,
      path: event.path || null,
      ts: new Date().toISOString(),
    });
  }
  queue.push({
    name: String(event.name).slice(0, 64),
    surface: String(event.surface).slice(0, 32),
    path: event.path != null ? String(event.path).slice(0, 512) : undefined,
    org_id: event.org_id || undefined,
    props: event.props || undefined,
    ts: new Date().toISOString(),
  });
  if (queue.length >= MAX_BATCH) {
    void flushAnalytics();
  } else {
    scheduleFlush();
  }
}

export function trackPageView(pathname, isAuthenticated) {
  const path = String(pathname || "/");
  if (path === lastPathTracked) return;
  lastPathTracked = path;
  const surface = resolveAnalyticsSurface(path, !!isAuthenticated);
  trackAnalyticsEvent({
    name: "page_view",
    surface,
    path,
  });
}

export async function flushAnalytics() {
  if (isAnalyticsOptedOut()) {
    queue = [];
    return;
  }
  if (!queue.length) return;
  const batch = queue.splice(0, MAX_BATCH);
  const body = JSON.stringify({
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    events: batch,
  });
  const url = apiUrl("/analytics/events");
  const token = getAccessToken();
  const headers = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;

  try {
    if (typeof navigator !== "undefined" && navigator.sendBeacon && !token) {
      const blob = new Blob([body], { type: "application/json" });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return;
    }
    await fetch(url, {
      method: "POST",
      headers,
      body,
      keepalive: true,
      credentials: "omit",
    });
  } catch {
    /* drop — analytics must not break UX */
  }
}

export function installAnalyticsLifecycle() {
  if (typeof window === "undefined") return () => {};
  const onHide = () => {
    void flushAnalytics();
  };
  window.addEventListener("pagehide", onHide);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") onHide();
  });
  return () => {
    window.removeEventListener("pagehide", onHide);
  };
}

/** Test helper */
export function _resetAnalyticsForTests() {
  queue = [];
  lastPathTracked = null;
  sessionStartSent = false;
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
}
